import { CSSProperties, FC, memo, useEffect, useRef } from 'react'
import { Page } from 'react-pdf'
import { FixedSizeList as List } from 'react-window'
import './PdfViewer.css'
import { cx } from '@/utils/cx'
import { devLog } from '@/methods/devLog'

const parse = (value: string | number | undefined, reduceValue: number) => {
  if (!value) return '700px'
  if (value === '100%') {
    return value
  }

  if (typeof value === 'string') {
    return `${Number(value.replace('px', '')) - reduceValue}px`
  } else {
    return `${value - reduceValue}px`
  }
}

interface RowProps {
  index: number
  style: CSSProperties
}

interface PageRendererProps {
  numPages: number
  searchText?: string
  pageClassName?: string
  canvasWidth: number
  pageScale: number
  containerDimensions?: { width: number; height: number }
  viewerWidth?: string | number
  viewerHeight?: string | number
  onDocumentLastPage?: () => void
  setCurrentPageIndex?: (index: number) => void
  handlePageIndicatorVisibility?: () => void
  onFocusHandler?: (isFocused: boolean) => void
}

export const PageRenderer: FC<PageRendererProps> = memo(
  ({
    numPages,
    pageClassName,
    canvasWidth,
    pageScale,
    containerDimensions,
    viewerWidth,
    viewerHeight,
    onDocumentLastPage,
    setCurrentPageIndex,
    handlePageIndicatorVisibility,
    onFocusHandler,
  }) => {
    const scrollContainerRef = useRef<HTMLDivElement>(null)

    const highestScrolledPageRef = useRef(0)

    // Render function for react-window
    const ReactWindowPage = ({ index, style }: RowProps) => {
      const targetRef = useRef(null)

      useEffect(() => {
        const observer = new IntersectionObserver(
          ([entry]) => {
            devLog('OBSERVING', entry.isIntersecting, index + 1)

            if (entry.isIntersecting) {
              setCurrentPageIndex && setCurrentPageIndex(index + 1)

              if (index + 1 > highestScrolledPageRef.current) {
                highestScrolledPageRef.current = index + 1
              }
            }
          },
          {
            root: null, // Set to a parent container if needed, or leave `null` for the viewport
            threshold: 0.1, // Percentage of the element that needs to be visible
          }
        )

        if (targetRef.current) {
          observer.observe(targetRef.current)
        }

        return () => {
          if (targetRef.current) {
            observer.unobserve(targetRef.current)
          }
        }
      }, [])

      return (
        <div
          className={cx(
            'pdf-old__page-container page-item',
            pageScale > 0.6000000000000001 && 'pdf-old__page-container--not-centered'
          )}
          style={style}
          ref={targetRef}
          tabIndex={0}
          onFocus={() => onFocusHandler && onFocusHandler(true)}
          onBlur={() => onFocusHandler && onFocusHandler(false)}
        >
          <Page
            devicePixelRatio={Math.min(2, window.devicePixelRatio)}
            className={pageClassName}
            pageNumber={index + 1}
            width={canvasWidth}
            scale={pageScale}
          />
        </div>
      )
    }

    const onScrollHandler = () => {
      handlePageIndicatorVisibility && handlePageIndicatorVisibility()

      highestScrolledPageRef.current === numPages && devLog('highestScrolledPage')

      highestScrolledPageRef.current === numPages && onDocumentLastPage && onDocumentLastPage()
    }

    return (
      <div
        className="pdf-old__inner-wrap onboarding-pdf-viewer-inner-wrap"
        ref={scrollContainerRef}
        style={
          {
            '--iw-height': parse(viewerHeight, 20),
            '--iw-height-mobile': parse(viewerHeight, 0),
          } as CSSProperties
        }
      >
        <List
          height={containerDimensions?.height ?? 0}
          itemSize={(containerDimensions?.width ?? 0) * 1.414 * pageScale}
          itemCount={numPages}
          width={containerDimensions?.width ?? 0}
          onScroll={onScrollHandler}
          overscanCount={1}
        >
          {ReactWindowPage}
        </List>
      </div>
    )
  }
)
